import { useState, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { v4 as uuidv4 } from "uuid";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Loader2,
  AlertCircle,
  Upload,
  Check,
  AlertTriangle,
  X,
  FileText,
  Plus,
} from "lucide-react";
import { useProviderJobOffers } from "../hooks/useProviderJobOffers";
import { useCreateExpense } from "../hooks/useCreateExpense";
import { useMarkReceiptsUploaded } from "../hooks/useMarkReceiptsUploaded";
import {
  getExpenseCategoryOptions,
  type ExpenseCategoryType,
} from "../utils/expense-schema";
import { useTRPC } from "@/trpc";
import { toast } from "sonner";
import { uploadReservedReceipt } from "../utils/receipt-utils";

interface BatchExpenseSubmitDialogProps {
  children?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

interface ParsedReceipt {
  amount: string | null;
  expenseDate: string | null;
  suggestedCategory: ExpenseCategoryType | null;
  merchantName: string | null;
}

interface ParseResult {
  receiptData: ParsedReceipt;
  warnings: string[];
  fileName: string;
}

interface FileParseState {
  id: string;
  file: File;
  status: "pending" | "parsing" | "parsed" | "error" | "could-not-parse";
  parseResult?: ParseResult;
  error?: string;
}

interface ExpenseEntry {
  id: string;
  fileParseStateIds: string[]; // IDs of FileParseState entries for this expense
  fileName: string | null; // Display name (can be comma-separated for multiple)
  amount: string;
  expenseDate: string;
  category: ExpenseCategoryType;
  notes: string;
  warnings: string[];
  selected: boolean;
}

type WizardStep = "upload" | "review" | "submitting";

const ACCEPTED_FILE_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/heic",
  "image/webp",
].join(",");

export function BatchExpenseSubmitDialog({
  children,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: BatchExpenseSubmitDialogProps) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<WizardStep>("upload");
  const [jobOfferId, setJobOfferId] = useState<string>("");
  const [fileParseStates, setFileParseStates] = useState<FileParseState[]>([]);
  const [expenseEntries, setExpenseEntries] = useState<ExpenseEntry[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [submissionProgress, setSubmissionProgress] = useState({
    current: 0,
    total: 0,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const trpc = useTRPC();
  const { data: providerUser } = useQuery(
    trpc.provider.getProviderUser.queryOptions(),
  );
  const { jobOffers, isLoading: loadingJobOffers } = useProviderJobOffers();
  const { mutateAsync: createExpense } = useCreateExpense();
  const { mutateAsync: markReceiptsUploaded } = useMarkReceiptsUploaded();

  const parseMutation = useMutation(
    trpc.expense.parseReceiptFile.mutationOptions(),
  );

  const categoryOptions = getExpenseCategoryOptions();

  const isOpen = controlledOpen !== undefined ? controlledOpen : open;
  const setIsOpen = controlledOnOpenChange || setOpen;

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    handleFilesAdded(files);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    handleFilesAdded(files);
    // Reset input so same file can be selected again
    e.target.value = "";
  };

  const handleFilesAdded = async (files: File[]) => {
    // Create file parse states
    const newFileParseStates: FileParseState[] = files.map((file) => ({
      id: uuidv4(),
      file,
      status: "parsing",
    }));

    setFileParseStates((prev) => [...prev, ...newFileParseStates]);

    // Parse each file in parallel
    newFileParseStates.forEach(async (fileParseState) => {
      try {
        const formData = new FormData();
        formData.append("file", fileParseState.file);

        const result = await parseMutation.mutateAsync(formData);

        // Update file parse state
        setFileParseStates((prev) =>
          prev.map((fps) =>
            fps.id === fileParseState.id
              ? {
                  ...fps,
                  status:
                    result.receiptData.amount === null
                      ? "could-not-parse"
                      : "parsed",
                  parseResult: result,
                }
              : fps,
          ),
        );

        // Create expense entry if parsed successfully
        if (result.receiptData.amount !== null) {
          const newEntry: ExpenseEntry = {
            id: uuidv4(),
            fileParseStateIds: [fileParseState.id],
            fileName: fileParseState.file.name,
            amount: result.receiptData.amount || "",
            expenseDate: result.receiptData.expenseDate || "",
            category: result.receiptData.suggestedCategory || "other",
            notes: result.receiptData.merchantName
              ? `Merchant: ${result.receiptData.merchantName}`
              : "",
            warnings: result.warnings || [],
            selected: false,
          };
          setExpenseEntries((prev) => [...prev, newEntry]);
        }
      } catch (error) {
        // Update file parse state with error
        setFileParseStates((prev) =>
          prev.map((fps) =>
            fps.id === fileParseState.id
              ? {
                  ...fps,
                  status: "error",
                  error:
                    error instanceof Error
                      ? error.message
                      : "Failed to parse receipt",
                }
              : fps,
          ),
        );
      }
    });
  };

  const handleRemoveFile = (id: string) => {
    // Remove file parse state
    setFileParseStates((prev) => prev.filter((fps) => fps.id !== id));

    // Remove file from expense entries that reference it
    // If an entry only had this file, remove the whole entry
    setExpenseEntries((prev) =>
      prev
        .map((entry) => ({
          ...entry,
          fileParseStateIds: entry.fileParseStateIds.filter(
            (fid) => fid !== id,
          ),
        }))
        .filter((entry) => entry.fileParseStateIds.length > 0),
    );
  };

  const handleAddManualEntry = () => {
    const newEntry: ExpenseEntry = {
      id: uuidv4(),
      fileParseStateIds: [],
      fileName: null,
      amount: "",
      expenseDate: "",
      category: "other",
      notes: "",
      warnings: [],
      selected: false,
    };
    setExpenseEntries((prev) => [...prev, newEntry]);

    // If we're on upload step and adding manual entry, move to review
    if (step === "upload") {
      setStep("review");
    }
  };

  const handleRemoveExpenseEntry = (id: string) => {
    setExpenseEntries((prev) => prev.filter((entry) => entry.id !== id));
  };

  const handleUpdateExpenseEntry = (
    id: string,
    updates: Partial<ExpenseEntry>,
  ) => {
    setExpenseEntries((prev) =>
      prev.map((entry) => (entry.id === id ? { ...entry, ...updates } : entry)),
    );
  };

  const handleToggleSelection = (id: string) => {
    setExpenseEntries((prev) =>
      prev.map((entry) =>
        entry.id === id ? { ...entry, selected: !entry.selected } : entry,
      ),
    );
  };

  // Handle adding files to an existing expense entry (for manual entries)
  const handleAddFilesToEntry = async (entryId: string, files: File[]) => {
    // Create file parse states for the new files
    const newFileParseStates: FileParseState[] = files.map((file) => ({
      id: uuidv4(),
      file,
      status: "parsing",
    }));

    setFileParseStates((prev) => [...prev, ...newFileParseStates]);

    // Add file IDs to the entry immediately
    setExpenseEntries((prev) =>
      prev.map((entry) =>
        entry.id === entryId
          ? {
              ...entry,
              fileParseStateIds: [
                ...entry.fileParseStateIds,
                ...newFileParseStates.map((fps) => fps.id),
              ],
              fileName: entry.fileName
                ? `${entry.fileName}, ${files.map((f) => f.name).join(", ")}`
                : files.map((f) => f.name).join(", "),
            }
          : entry,
      ),
    );

    // Parse each file (for potential data extraction, but don't override entry data)
    newFileParseStates.forEach(async (fileParseState) => {
      try {
        const formData = new FormData();
        formData.append("file", fileParseState.file);

        const result = await parseMutation.mutateAsync(formData);

        // Update file parse state
        setFileParseStates((prev) =>
          prev.map((fps) =>
            fps.id === fileParseState.id
              ? {
                  ...fps,
                  status:
                    result.receiptData.amount === null
                      ? "could-not-parse"
                      : "parsed",
                  parseResult: result,
                }
              : fps,
          ),
        );
      } catch (error) {
        // Update file parse state with error but keep the file attached
        setFileParseStates((prev) =>
          prev.map((fps) =>
            fps.id === fileParseState.id
              ? {
                  ...fps,
                  status: "error",
                  error:
                    error instanceof Error
                      ? error.message
                      : "Failed to parse receipt",
                }
              : fps,
          ),
        );
      }
    });
  };

  const handleMergeSelected = () => {
    const selectedEntries = expenseEntries.filter((e) => e.selected);
    if (selectedEntries.length < 2) {
      toast.error("Please select at least 2 expenses to merge");
      return;
    }

    // Calculate total amount
    const totalAmount = selectedEntries
      .reduce((sum, entry) => {
        const amount = parseFloat(entry.amount) || 0;
        return sum + amount;
      }, 0)
      .toFixed(2);

    // Use the earliest date
    const earliestDate = selectedEntries
      .map((e) => e.expenseDate)
      .filter((d) => d)
      .sort()[0];

    // Combine notes
    const combinedNotes = selectedEntries
      .map((e) => e.notes)
      .filter((n) => n)
      .join("; ");

    // Combine warnings
    const combinedWarnings = selectedEntries.flatMap((e) => e.warnings);

    // Combine file IDs from all selected entries
    const combinedFileIds = selectedEntries.flatMap((e) => e.fileParseStateIds);

    // Combine file names for display
    const fileNames = selectedEntries
      .map((e) => e.fileName)
      .filter((n) => n)
      .join(", ");

    // Create merged entry
    const mergedEntry: ExpenseEntry = {
      id: uuidv4(),
      fileParseStateIds: combinedFileIds,
      fileName: fileNames || null,
      amount: totalAmount,
      expenseDate: earliestDate || "",
      category: selectedEntries[0].category,
      notes: combinedNotes,
      warnings: combinedWarnings,
      selected: false,
    };

    // Remove selected entries and add merged entry
    setExpenseEntries((prev) => [
      ...prev.filter((e) => !e.selected),
      mergedEntry,
    ]);

    toast.success(`Merged ${selectedEntries.length} expenses`);
  };

  const handleContinueToReview = () => {
    if (!jobOfferId) {
      toast.error("Please select a job assignment");
      return;
    }
    if (fileParseStates.length === 0 && expenseEntries.length === 0) {
      toast.error("Please upload at least one receipt or add a manual entry");
      return;
    }
    setStep("review");
  };

  const handleBackToUpload = () => {
    setStep("upload");
  };

  const handleSubmitAll = async () => {
    if (!providerUser) {
      toast.error("User not found");
      return;
    }

    // Validate all entries have required fields
    const invalidFieldEntries = expenseEntries.filter(
      (entry) =>
        !entry.amount ||
        !entry.expenseDate ||
        !entry.category ||
        parseFloat(entry.amount) <= 0,
    );

    if (invalidFieldEntries.length > 0) {
      toast.error(
        "Please fill in all required fields (amount, date, category) for all expenses",
      );
      return;
    }

    // Validate all entries have at least one receipt file that exists in fileParseStates
    const entriesWithoutReceipts = expenseEntries.filter((entry) => {
      // Check that at least one file ID resolves to an actual file
      const resolvedFiles = entry.fileParseStateIds
        .map((id) => fileParseStates.find((fps) => fps.id === id))
        .filter((fps) => fps !== undefined);
      return resolvedFiles.length === 0;
    });

    if (entriesWithoutReceipts.length > 0) {
      console.error("Entries without receipts:", {
        entries: entriesWithoutReceipts.map((e) => ({
          id: e.id,
          fileParseStateIds: e.fileParseStateIds,
        })),
        fileParseStates: fileParseStates.map((fps) => ({
          id: fps.id,
          fileName: fps.file.name,
          status: fps.status,
        })),
      });
      toast.error(
        "All expenses require at least one receipt file. Please attach receipts to all entries.",
      );
      return;
    }

    setStep("submitting");
    setSubmissionProgress({ current: 0, total: expenseEntries.length });

    let successCount = 0;
    const failedExpenses: { entry: ExpenseEntry; error: string }[] = [];

    // Submit each expense
    for (let i = 0; i < expenseEntries.length; i++) {
      const entry = expenseEntries[i];
      setSubmissionProgress({ current: i + 1, total: expenseEntries.length });

      try {
        // Get all files for this entry
        const entryFileParseStates = entry.fileParseStateIds
          .map((id) => fileParseStates.find((fps) => fps.id === id))
          .filter((fps): fps is FileParseState => fps !== undefined);

        // Build receipts array from files
        const receipts = entryFileParseStates.map((fps) => ({
          fileName: fps.file.name,
        }));

        if (receipts.length === 0) {
          throw new Error("At least one receipt file is required");
        }

        // Create expense
        const result = await createExpense({
          jobOfferId,
          category: entry.category as Parameters<
            typeof createExpense
          >[0]["category"],
          amount: entry.amount,
          expenseDate: entry.expenseDate,
          receipts,
          providerNote: entry.notes || undefined,
        });

        // Upload all files. Each attempt reserves new receipts with fresh
        // paths and single-use tokens, so a failed entry is retried by
        // submitting it again rather than overwriting an earlier upload.
        if (result.receipts.length > 0) {
          for (let j = 0; j < result.receipts.length; j++) {
            const receiptRecord = result.receipts[j];
            const fileParseState = entryFileParseStates[j];

            if (fileParseState) {
              await uploadReservedReceipt({
                file: fileParseState.file,
                receipt: receiptRecord,
              });
            }
          }

          // Mark all receipts as uploaded
          await markReceiptsUploaded({
            receiptIds: result.receipts.map((r) => r.id),
          });
        }

        successCount++;
      } catch (error) {
        failedExpenses.push({
          entry,
          error:
            error instanceof Error ? error.message : "Failed to create expense",
        });
      }
    }

    // Show results
    if (failedExpenses.length === 0) {
      toast.success(`Successfully submitted ${successCount} expense(s)`);
      handleReset();
      setIsOpen(false);
    } else {
      toast.error(
        `Submitted ${successCount} expense(s), but ${failedExpenses.length} failed`,
      );
      // Remove successful entries, keep failed ones
      const failedIds = failedExpenses.map((f) => f.entry.id);
      setExpenseEntries((prev) => prev.filter((e) => failedIds.includes(e.id)));
      setStep("review");
    }
  };

  const handleReset = () => {
    setStep("upload");
    setJobOfferId("");
    setFileParseStates([]);
    setExpenseEntries([]);
    setSubmissionProgress({ current: 0, total: 0 });
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      handleReset();
    }
    setIsOpen(newOpen);
  };

  const canContinueToReview =
    jobOfferId && (fileParseStates.length > 0 || expenseEntries.length > 0);

  const runningTotal = expenseEntries.reduce((sum, entry) => {
    const amount = parseFloat(entry.amount) || 0;
    return sum + amount;
  }, 0);

  const selectedCount = expenseEntries.filter((e) => e.selected).length;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent className="sm:max-w-4xl dialog-safe-height overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Submit Expenses</DialogTitle>
          <DialogDescription>
            {step === "upload" &&
              "Upload receipt files or add manual entries for batch processing"}
            {step === "review" &&
              "Review and edit your expenses before submitting"}
            {step === "submitting" && "Submitting your expenses..."}
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Upload & Parse */}
        {step === "upload" && (
          <div className="space-y-4">
            {/* Job Offer Selection */}
            <div className="space-y-2">
              <Label htmlFor="jobOfferId">
                Job Assignment <span className="text-red-500">*</span>
              </Label>
              {loadingJobOffers ? (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading job assignments...
                </div>
              ) : jobOffers.length === 0 ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    You don't have any active job assignments. You can only
                    submit expenses for jobs you've claimed shifts for.
                  </AlertDescription>
                </Alert>
              ) : (
                <Select value={jobOfferId} onValueChange={setJobOfferId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a job assignment" />
                  </SelectTrigger>
                  <SelectContent>
                    {jobOffers.map((offer) => (
                      <SelectItem
                        key={offer.jobOfferId}
                        value={offer.jobOfferId}
                      >
                        <div className="flex flex-col">
                          <span>
                            {offer.jobOfferTitle || offer.facilityName}
                          </span>
                          <span className="text-xs text-slate-500">
                            {offer.facilityCity && offer.facilityState
                              ? `${offer.facilityCity}, ${offer.facilityState}`
                              : offer.facilityName}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* File Upload Area */}
            <div className="space-y-2">
              <Label>Receipt Files</Label>
              <div
                className={`
                  relative border-2 border-dashed rounded-lg p-8 text-center transition-colors
                  ${dragActive ? "border-blue-500 bg-blue-50" : "border-gray-300"}
                `}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_FILE_TYPES}
                  onChange={handleFileInputChange}
                  className="hidden"
                  multiple
                />

                <div
                  className="cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-10 w-10 mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-600 mb-1">
                    Drag and drop receipt files here, or click to browse
                  </p>
                  <p className="text-sm text-gray-400">
                    Supports PDF and image files (PNG, JPEG, HEIC, WebP)
                  </p>
                </div>
              </div>
            </div>

            {/* File Parse States */}
            {fileParseStates.length > 0 && (
              <div className="space-y-2">
                <Label>Uploaded Files ({fileParseStates.length})</Label>
                <div className="space-y-2">
                  {fileParseStates.map((fps) => (
                    <div
                      key={fps.id}
                      className="flex items-center gap-3 p-3 border rounded-lg"
                    >
                      <FileText className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {fps.file.name}
                        </p>
                        {fps.status === "parsing" && (
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Parsing...
                          </div>
                        )}
                        {fps.status === "parsed" && fps.parseResult && (
                          <div className="flex items-center gap-2 text-xs text-green-600">
                            <Check className="h-3 w-3" />
                            Parsed: ${
                              fps.parseResult.receiptData.amount
                            } on {fps.parseResult.receiptData.expenseDate} (
                            {fps.parseResult.receiptData.suggestedCategory})
                            {fps.parseResult.receiptData.merchantName &&
                              ` - ${fps.parseResult.receiptData.merchantName}`}
                          </div>
                        )}
                        {fps.status === "could-not-parse" && (
                          <div className="flex items-center gap-2 text-xs text-amber-600">
                            <AlertTriangle className="h-3 w-3" />
                            Could not parse - will need manual entry
                          </div>
                        )}
                        {fps.status === "error" && (
                          <div className="flex items-center gap-2 text-xs text-red-600">
                            <AlertCircle className="h-3 w-3" />
                            Error: {fps.error}
                          </div>
                        )}
                        {fps.parseResult &&
                          fps.parseResult.warnings.length > 0 && (
                            <div className="mt-1">
                              {fps.parseResult.warnings.map((warning, i) => (
                                <p key={i} className="text-xs text-amber-600">
                                  Warning: {warning}
                                </p>
                              ))}
                            </div>
                          )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveFile(fps.id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={handleAddManualEntry}
                className="w-full sm:w-auto"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Manual Entry
              </Button>
              <Button
                type="button"
                onClick={handleContinueToReview}
                disabled={!canContinueToReview}
                className="w-full sm:w-auto"
              >
                Continue to Review
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Review & Edit */}
        {step === "review" && (
          <div className="space-y-4">
            {/* Merge Controls */}
            {selectedCount > 0 && (
              <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <span className="text-sm font-medium">
                  {selectedCount} expense(s) selected
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleMergeSelected}
                  disabled={selectedCount < 2}
                >
                  Merge Selected
                </Button>
              </div>
            )}

            {/* Expense Entries */}
            <div className="space-y-3">
              {expenseEntries.map((entry) => (
                <div key={entry.id} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={entry.selected}
                      onCheckedChange={() => handleToggleSelection(entry.id)}
                      className="mt-1"
                    />
                    <div className="flex-1 space-y-3">
                      {/* File Info or Upload Section */}
                      {entry.fileParseStateIds.length > 0 ? (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <FileText className="h-4 w-4" />
                          {entry.fileName}
                          {entry.fileParseStateIds.length > 1 && (
                            <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                              {entry.fileParseStateIds.length} files
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="border-2 border-dashed border-amber-300 bg-amber-50 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-sm text-amber-700 mb-2">
                            <AlertTriangle className="h-4 w-4" />
                            Receipt required
                          </div>
                          <input
                            type="file"
                            accept={ACCEPTED_FILE_TYPES}
                            multiple
                            className="text-sm file:mr-4 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-amber-100 file:text-amber-700 hover:file:bg-amber-200"
                            onChange={(e) => {
                              const files = e.target.files
                                ? Array.from(e.target.files)
                                : [];
                              if (files.length > 0) {
                                handleAddFilesToEntry(entry.id, files);
                              }
                              e.target.value = "";
                            }}
                          />
                        </div>
                      )}

                      {/* Warnings */}
                      {entry.warnings.length > 0 && (
                        <Alert variant="destructive" className="py-2">
                          <AlertTriangle className="h-4 w-4" />
                          <AlertDescription className="text-xs">
                            {entry.warnings.map((warning, i) => (
                              <div key={i}>{warning}</div>
                            ))}
                          </AlertDescription>
                        </Alert>
                      )}

                      {/* Form Fields */}
                      <div className="grid grid-cols-2 gap-3">
                        {/* Amount */}
                        <div className="space-y-1">
                          <Label className="text-xs">
                            Amount <span className="text-red-500">*</span>
                          </Label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">
                              $
                            </span>
                            <Input
                              type="text"
                              placeholder="0.00"
                              className="pl-7"
                              value={entry.amount}
                              onChange={(e) =>
                                handleUpdateExpenseEntry(entry.id, {
                                  amount: e.target.value,
                                })
                              }
                            />
                          </div>
                        </div>

                        {/* Date */}
                        <div className="space-y-1">
                          <Label className="text-xs">
                            Date <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            type="date"
                            value={entry.expenseDate}
                            onChange={(e) =>
                              handleUpdateExpenseEntry(entry.id, {
                                expenseDate: e.target.value,
                              })
                            }
                          />
                        </div>

                        {/* Category */}
                        <div className="space-y-1 col-span-2">
                          <Label className="text-xs">
                            Category <span className="text-red-500">*</span>
                          </Label>
                          <Select
                            value={entry.category}
                            onValueChange={(value) =>
                              handleUpdateExpenseEntry(entry.id, {
                                category: value as ExpenseCategoryType,
                              })
                            }
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {categoryOptions.map((option) => (
                                <SelectItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Notes */}
                        <div className="space-y-1 col-span-2">
                          <Label className="text-xs">Notes (Optional)</Label>
                          <Textarea
                            placeholder="Add any additional details..."
                            rows={2}
                            value={entry.notes}
                            onChange={(e) =>
                              handleUpdateExpenseEntry(entry.id, {
                                notes: e.target.value,
                              })
                            }
                          />
                        </div>
                      </div>
                    </div>

                    {/* Remove Button */}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveExpenseEntry(entry.id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Add Manual Entry Button */}
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleAddManualEntry}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Manual Entry
            </Button>

            {/* Running Total */}
            <div className="p-4 bg-slate-50 border rounded-lg">
              <div className="flex items-center justify-between text-lg font-semibold">
                <span>Total:</span>
                <span>${runningTotal.toFixed(2)}</span>
              </div>
              <p className="text-sm text-slate-600 mt-1">
                {expenseEntries.length} expense
                {expenseEntries.length !== 1 ? "s" : ""}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={handleBackToUpload}
                className="w-full sm:w-auto"
              >
                Back
              </Button>
              <Button
                type="button"
                onClick={handleSubmitAll}
                disabled={expenseEntries.length === 0}
                className="w-full sm:w-auto"
              >
                Submit All ({expenseEntries.length})
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Submitting */}
        {step === "submitting" && (
          <div className="space-y-4 py-8">
            <div className="text-center">
              <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
              <p className="text-lg font-medium">Submitting expenses...</p>
              <p className="text-sm text-gray-600 mt-1">
                {submissionProgress.current} of {submissionProgress.total}{" "}
                expenses created
              </p>
            </div>
            <Progress
              value={
                (submissionProgress.current / submissionProgress.total) * 100
              }
              className="w-full"
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
